import React, { useState } from 'react';
import { 
  Package, 
  Edit, 
  Trash2, 
  Plus, 
  Search, 
  Filter,
  Download,
  Upload,
  Star,
  TrendingUp,
  AlertCircle,
  Check,
  X,
  Save,
  Image,
  DollarSign,
  BarChart3,
  Eye,
  Copy,
  MoreVertical
} from 'lucide-react';

// Import the useDrawer hook (this would come from your main dashboard)
// For this example, I'm creating a mock version
import { useDrawer } from './manuela';

// Product Edit Form Component
const ProductEditForm = ({ product, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: product.name,
    price: product.price,
    category: product.category,
    stock: product.stock,
    status: product.status,
    description: product.description || ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="space-y-6">
      {/* Product Image */}
      <div className="flex justify-center">
        <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
          <Image className="w-12 h-12 text-gray-400" />
        </div>
      </div>

      {/* Basic Info */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Product Name
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stock
            </label>
            <input
              type="number"
              name="stock"
              value={formData.stock}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="Electronics">Electronics</option>
            <option value="Clothing">Clothing</option>
            <option value="Books">Books</option>
            <option value="Home">Home</option>
            <option value="Sports">Sports</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="Active">Active</option>
            <option value="Draft">Draft</option>
            <option value="Out of Stock">Out of Stock</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="4"
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter product description..."
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 pt-4 border-t">
        <button 
          onClick={() => onSave(formData)}
          className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          Save Changes
        </button>
        <button 
          onClick={() => console.log('Delete product')}
          className="px-4 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      </div>
    </div>
  );
};

// Product Analytics Component
const ProductAnalytics = ({ product }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 text-blue-600 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm font-medium">Views</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">1,234</p>
          <p className="text-xs text-gray-600">+12% from last week</p>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 text-green-600 mb-1">
            <DollarSign className="w-4 h-4" />
            <span className="text-sm font-medium">Revenue</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">$5,678</p>
          <p className="text-xs text-gray-600">+8% from last week</p>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="font-medium text-gray-900">Performance Metrics</h3>
        
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Conversion Rate</span>
            <span className="font-medium">3.2%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full" style={{ width: '32%' }}></div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Customer Satisfaction</span>
            <span className="font-medium">4.5/5</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-green-600 h-2 rounded-full" style={{ width: '90%' }}></div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Stock Level</span>
            <span className="font-medium">65%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '65%' }}></div>
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <h3 className="font-medium text-gray-900 mb-3">Recent Activity</h3>
        <div className="space-y-2">
          <div className="flex items-center gap-3 text-sm">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-gray-600">5 units sold today</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-gray-600">Price updated 2 days ago</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span className="text-gray-600">Low stock alert</span>
          </div>
        </div>
      </div>

      <button className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2">
        <Download className="w-4 h-4" />
        Export Full Report
      </button>
    </div>
  );
};

// Main Products Page Component
const TestPage = () => {
  const { openDrawer } = useDrawer();
  const [selectedFilter, setSelectedFilter] = useState('all');
  
  // Sample product data
  const products = [
    {
      id: 1,
      name: 'Wireless Headphones',
      category: 'Electronics',
      price: 99.99,
      stock: 45,
      status: 'Active',
      rating: 4.5,
      sales: 234
    },
    {
      id: 2,
      name: 'Running Shoes',
      category: 'Sports',
      price: 79.99,
      stock: 12,
      status: 'Active',
      rating: 4.2,
      sales: 156
    },
    {
      id: 3,
      name: 'Coffee Maker',
      category: 'Home',
      price: 149.99,
      stock: 0,
      status: 'Out of Stock',
      rating: 4.8,
      sales: 89
    },
    {
      id: 4,
      name: 'Yoga Mat',
      category: 'Sports',
      price: 29.99,
      stock: 78,
      status: 'Active',
      rating: 4.6,
      sales: 445
    },
    {
      id: 5,
      name: 'Smart Watch',
      category: 'Electronics',
      price: 299.99,
      stock: 23,
      status: 'Active',
      rating: 4.3,
      sales: 67
    }
  ];

  // Handler functions
  const handleEditProduct = (product) => {
    openDrawer(
      `Edit Product: ${product.name}`,
      <ProductEditForm 
        product={product} 
        onSave={(data) => {
          console.log('Saving product:', data);
          // Handle save logic here
        }}
      />
    );
  };

  const handleViewAnalytics = (product) => {
    openDrawer(
      `Analytics: ${product.name}`,
      <ProductAnalytics product={product} />
    );
  };

  const handleAddProduct = () => {
    const newProduct = {
      id: 0,
      name: '',
      category: 'Electronics',
      price: 0,
      stock: 0,
      status: 'Draft',
      description: ''
    };
    
    openDrawer(
      'Add New Product',
      <ProductEditForm 
        product={newProduct}
        onSave={(data) => {
          console.log('Creating new product:', data);
          // Handle creation logic here
        }}
      />
    );
  };

  const handleBulkAction = () => {
    openDrawer(
      'Bulk Actions',
      <div className="space-y-4">
        <p className="text-gray-600">Select an action to apply to selected products:</p>
        <div className="space-y-2">
          <button className="w-full text-left px-4 py-3 border rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3">
              <Edit className="w-4 h-4 text-gray-500" />
              <div>
                <p className="font-medium">Edit Categories</p>
                <p className="text-sm text-gray-500">Change category for multiple products</p>
              </div>
            </div>
          </button>
          <button className="w-full text-left px-4 py-3 border rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3">
              <DollarSign className="w-4 h-4 text-gray-500" />
              <div>
                <p className="font-medium">Adjust Prices</p>
                <p className="text-sm text-gray-500">Apply percentage or fixed price changes</p>
              </div>
            </div>
          </button>
          <button className="w-full text-left px-4 py-3 border rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3">
              <Download className="w-4 h-4 text-gray-500" />
              <div>
                <p className="font-medium">Export Selected</p>
                <p className="text-sm text-gray-500">Download product data as CSV</p>
              </div>
            </div>
          </button>
        </div>
      </div>
    );
  };

  // Get status color
  const getStatusColor = (status) => {
    switch(status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Out of Stock':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Products</h1>
            <p className="text-gray-600 mt-1">Manage your product inventory and listings</p>
          </div>
          <button
            onClick={handleAddProduct}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Product
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Total Products</span>
            <Package className="w-4 h-4 text-gray-400" />
          </div>
          <p className="text-2xl font-bold">156</p>
          <p className="text-xs text-green-600">+12 this month</p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Total Revenue</span>
            <DollarSign className="w-4 h-4 text-gray-400" />
          </div>
          <p className="text-2xl font-bold">$45,678</p>
          <p className="text-xs text-green-600">+18% from last month</p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Low Stock</span>
            <AlertCircle className="w-4 h-4 text-yellow-500" />
          </div>
          <p className="text-2xl font-bold">8</p>
          <p className="text-xs text-yellow-600">Needs attention</p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Avg Rating</span>
            <Star className="w-4 h-4 text-gray-400" />
          </div>
          <p className="text-2xl font-bold">4.5</p>
          <p className="text-xs text-gray-600">From 1,234 reviews</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={handleBulkAction}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Bulk Actions
              </button>
              
              <button className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Import
              </button>
              
              <button className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input type="checkbox" className="rounded" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sales
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input type="checkbox" className="rounded" />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                        <Package className="w-5 h-5 text-gray-400" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{product.name}</p>
                        <p className="text-sm text-gray-500">ID: #{product.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {product.category}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    ${product.price}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <span className={`text-sm ${product.stock < 20 ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                        {product.stock}
                      </span>
                      {product.stock < 20 && product.stock > 0 && (
                        <AlertCircle className="w-4 h-4 text-yellow-500 ml-2" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(product.status)}`}>
                      {product.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-400 mr-1" />
                      <span className="text-sm text-gray-600">{product.rating}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {product.sales}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEditProduct(product)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Edit Product"
                      >
                        <Edit className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => handleViewAnalytics(product)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="View Analytics"
                      >
                        <BarChart3 className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Duplicate Product"
                      >
                        <Copy className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="More Options"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing 1 to 5 of 156 products
            </p>
            <div className="flex gap-2">
              <button className="px-3 py-1 border rounded hover:bg-gray-50 transition-colors">
                Previous
              </button>
              <button className="px-3 py-1 bg-blue-600 text-white rounded">1</button>
              <button className="px-3 py-1 border rounded hover:bg-gray-50 transition-colors">2</button>
              <button className="px-3 py-1 border rounded hover:bg-gray-50 transition-colors">3</button>
              <button className="px-3 py-1 border rounded hover:bg-gray-50 transition-colors">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestPage;